import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import {
  isValidPhoneNumber,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  CountryCode
} from 'libphonenumber-js';
import { CountrySelectComponent } from '../country-select/country-select.component';

export interface CountryPhoneValue {
  country: string;
  phone: string;
}

@Component({
  selector: 'app-country-phone-input',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, CountrySelectComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryPhoneInputComponent),
      multi: true
    }
  ],
  templateUrl: './country-phone-input.component.html',
  styleUrls: ['./country-phone-input.component.scss']
})
export class CountryPhoneInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() phoneRequired = true;
  @Input() countryOnly = false;
  @Input() lockCountry = false;
  @Input() inputClass = 'form-input';
  @Output() validityChange = new EventEmitter<boolean>();

  form!: FormGroup;
  dialCode = '';
  phoneError = '';

  private destroy$ = new Subject<void>();
  private pendingValue: string | CountryPhoneValue | null = null;
  private onChange: (value: string | CountryPhoneValue) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    const initialCountry = this.lockCountry && this.pendingValue
      ? this.extractCountry(this.pendingValue)
      : 'US';

    this.form = this.fb.group({
      country: [{ value: initialCountry, disabled: this.lockCountry }, Validators.required],
      phone: ['', this.phoneRequired ? [Validators.required] : []]
    });

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateDialCode();
      this.validatePhone();
      this.emitValue();
    });

    if (this.pendingValue !== null) {
      this.applyValue(this.pendingValue);
      this.pendingValue = null;
    }

    this.updateDialCode();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private extractCountry(value: string | CountryPhoneValue): string {
    if (typeof value === 'string') {
      return value;
    }
    return value.country || 'US';
  }

  private applyValue(value: string | CountryPhoneValue): void {
    if (!this.form) {
      return;
    }

    if (typeof value === 'string') {
      this.patchCountryControl(value);
    } else {
      let country = value.country || 'US';
      let phone = (value.phone || '').trim();

      if (phone.startsWith('+')) {
        const parsed = parsePhoneNumberFromString(phone);
        if (parsed) {
          if (!this.lockCountry) {
            country = parsed.country || country;
          }
          phone = parsed.nationalNumber;
        }
      }

      this.patchCountryControl(country);
      this.form.patchValue({ phone }, { emitEvent: false });
    }
    this.updateDialCode();
    this.validatePhone();
  }

  private patchCountryControl(country: string): void {
    const countryControl = this.form.get('country');
    if (!countryControl) {
      return;
    }

    const wasDisabled = countryControl.disabled;
    if (wasDisabled) {
      countryControl.enable({ emitEvent: false });
    }
    countryControl.setValue(country, { emitEvent: false });
    if (wasDisabled) {
      countryControl.disable({ emitEvent: false });
    }
  }

  private updateDialCode(): void {
    const country = this.form.get('country')?.value as CountryCode;
    try {
      this.dialCode = country ? `+${getCountryCallingCode(country)}` : '';
    } catch {
      this.dialCode = '';
    }
  }

  private validatePhone(): void {
    if (this.countryOnly) {
      this.phoneError = '';
      this.validityChange.emit(this.form.get('country')?.valid ?? false);
      return;
    }

    const country = this.form.get('country')?.value;
    const phone = (this.form.get('phone')?.value || '').trim();

    if (!phone) {
      this.phoneError = this.phoneRequired ? 'Phone number is required' : '';
      this.validityChange.emit(!this.phoneRequired);
      return;
    }

    const fullNumber = phone.startsWith('+') ? phone : `${this.dialCode}${phone}`;
    if (country && isValidPhoneNumber(fullNumber, country as CountryCode)) {
      this.phoneError = '';
      this.validityChange.emit(true);
    } else {
      this.phoneError = 'Please enter a valid phone number';
      this.validityChange.emit(false);
    }
  }

  private emitValue(): void {
    const country = this.form.get('country')?.value || '';
    const phone = (this.form.get('phone')?.value || '').trim();

    if (this.countryOnly) {
      this.onChange(country);
      return;
    }

    const fullPhone = phone.startsWith('+') ? phone : (phone ? `${this.dialCode}${phone}` : '');
    this.onChange({ country, phone: fullPhone });
  }

  writeValue(value: string | CountryPhoneValue | null): void {
    if (!value) {
      return;
    }

    if (!this.form) {
      this.pendingValue = value;
      return;
    }

    this.applyValue(value);
  }

  registerOnChange(fn: (value: string | CountryPhoneValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!this.form) {
      return;
    }
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
      if (this.lockCountry) {
        this.form.get('country')?.disable({ emitEvent: false });
      }
    }
  }

  onBlur(): void {
    this.onTouched();
  }
}
